import { Trans, useLingui } from '@lingui/react/macro';
import { SignOut } from '@phosphor-icons/react';
import { ArrowCounterClockwise, ArrowRight, X } from '@remarkable/ark-icons';
import { getRouteApi } from '@tanstack/react-router';
import toast from 'react-hot-toast';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import {
  useAbortCancelSubscription,
  useCurrentMember,
  useSubscriptionSuspense,
} from 'src/api/queries';
import { subscriptionHelpers } from 'src/utils/subscriptionHelpers';
import { URLS } from 'src/utils/urls/urls';

import { Button, NotificationBox, Sheet } from '../../../../components';
import { AbortCancelSubscriptionModal } from './AbortCancelSubscriptionModal';
import { CancelSubscriptionButton } from './CancelSubscriptionButton';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import { ConnectSeatsDetails } from './ConnectPlanSeatsDetails';
import { ConnectSimpleDetails } from './ConnectPlanSimpleDetails';
import { FreeConnectDetails } from './FreeConnectDetails';
import { LeaveSubscriptionModal } from './LeaveSubscriptionModal';
import { NoSubscriptionDetails } from './NoSubscriptionCard';

const routeApi = getRouteApi('/_auth/_layout/subscriptions/plan');

export const SubscriptionCard = () => {
  const { t } = useLingui();
  const subscription = useSubscriptionSuspense();
  const abortCancelSubscription = useAbortCancelSubscription();
  const member = useCurrentMember();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const shouldSeeLeaveButton =
    member.data?.access.canRemoveUser &&
    !subscription.data?.access.canSeeMembers;
  const shouldSeePaymentButton =
    subscriptionHelpers.isActive(subscription.data) &&
    !subscriptionHelpers.isFreeConnect(subscription.data);

  const isCanceled = subscriptionHelpers.isCancelled(subscription.data);

  const isCancelFlowOpen =
    search.cancel_subscription !== undefined &&
    search.cancel_subscription !== 'confirmation';

  const canStartCancellation =
    subscription.data?.access.canCancel && !isCanceled;

  if (search.cancel_subscription === 'confirmation') {
    toast.success(t`Subscription cancelled`, {
      id: 'subscription-cancelled-toast',
    });
    void navigate({ search: { cancel_subscription: undefined } });
  }

  return (
    <Sheet className="gap-32" data-cy="subscription-card">
      <SubscriptionDetails />
      <div className="flex flex-wrap gap-24">
        {shouldSeePaymentButton && (
          <Button
            as="a"
            to={URLS.SUBSCRIPTION_PAYMENT}
            variant="primary"
            size="large"
            className="w-full ls:w-fit"
            data-cy="edit-payment-details-btn"
          >
            <span>
              <Trans>Edit payment details</Trans>
            </span>
            <ArrowRight />
          </Button>
        )}

        {shouldSeeLeaveButton && (
          <LeaveSubscriptionModal
            trigger={
              <Button
                variant="secondary"
                size="large"
                className="w-full ls:w-fit"
                analytics={{
                  text: 'Leave subscription',
                  location: ComponentLocations.MANAGE_CONNECT.SUBSCRIPTION_PAGE,
                  action: 'open leave subscription modal',
                }}
              >
                <SignOut />{' '}
                <span>
                  <Trans>Leave subscription</Trans>
                </span>
              </Button>
            }
          ></LeaveSubscriptionModal>
        )}

        {canStartCancellation &&
          subscriptionHelpers.isManagementTypeSeats(subscription.data) && (
            <CancelSubscriptionModal
              trigger={
                <Button
                  variant="tertiary-neutral"
                  size="large"
                  className="w-full ls:w-fit"
                  analytics={{
                    text: 'Cancel subscription',
                    location:
                      ComponentLocations.MANAGE_CONNECT.SUBSCRIPTION_PAGE,
                    action: 'open cancel subscription modal',
                  }}
                >
                  <X />
                  <span>
                    <Trans>Cancel subscription</Trans>
                  </span>
                </Button>
              }
            />
          )}

        {(canStartCancellation || isCancelFlowOpen) &&
          !subscriptionHelpers.isManagementTypeSeats(subscription.data) && (
            <CancelSubscriptionButton />
          )}

        {subscription.data?.access.canReactivate && isCanceled && (
          <AbortCancelSubscriptionModal
            isAborting={abortCancelSubscription.isPending}
            abortError={abortCancelSubscription.error}
            onAbort={() => {
              abortCancelSubscription.mutate();
            }}
            trigger={
              <Button
                variant="primary"
                size="large"
                analytics={{
                  text: 'Undo cancelation',
                  location: ComponentLocations.MANAGE_CONNECT.SUBSCRIPTION_PAGE,
                  action: 'open undo cancelation modal',
                }}
              >
                <ArrowCounterClockwise />
                <span>
                  <Trans>Undo cancelation</Trans>
                </span>
              </Button>
            }
          />
        )}
      </div>
    </Sheet>
  );
};

const SubscriptionDetails = () => {
  const subscriptions = useSubscriptionSuspense();

  if (subscriptions.isError) {
    return (
      <NotificationBox error={subscriptions.error}>
        <Trans>Error loading subscriptions</Trans>
      </NotificationBox>
    );
  }

  if (subscriptionHelpers.isStripe(subscriptions.data)) {
    if (subscriptions.data.managementType === 'seats') {
      return <ConnectSeatsDetails />;
    }

    return <ConnectSimpleDetails />;
  }

  if (subscriptionHelpers.isFreeConnect(subscriptions.data)) {
    return <FreeConnectDetails />;
  }

  return <NoSubscriptionDetails />;
};
